
import React from 'react';
import { motion } from 'framer-motion';
import { Mail, ArrowUpCircle, Linkedin, Twitter, Instagram, MapPin } from 'lucide-react';

interface FooterProps {
  currentView: 'home' | 'projects';
  setView: (view: 'home' | 'projects') => void;
}

const Footer: React.FC<FooterProps> = ({ currentView, setView }) => {
  const scrollToContact = (e: React.MouseEvent) => {
    e.preventDefault();
    
    const performScroll = () => {
      const element = document.getElementById('contact-form');
      if (element) {
        const headerOffset = 100;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    };

    if (currentView !== 'home') {
      setView('home');
      // Esperar a que la transición de Framer Motion comience y el componente se monte
      setTimeout(performScroll, 600);
    } else {
      performScroll();
    }
  };

  return (
    <footer id="contact" className="py-12 md:py-20 px-6 lg:px-20 glass-panel border-t border-white/5 mt-12 md:mt-20">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-10 md:gap-12 mb-16 md:mb-20">
          <div className="w-full lg:w-auto">
            <h2 className="font-syncopate text-3xl md:text-4xl lg:text-6xl font-bold mb-4 md:mb-6">¿LISTO PARA <br /> <span className="text-[#39FF14]">AVANZAR?</span></h2>
            <div className="space-y-4">
              <p className="text-gray-400 max-w-md uppercase tracking-widest text-[10px] md:text-xs font-bold">
                Actualmente aceptando nuevos proyectos y oportunidades de consultoría.
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-[10px] md:text-xs font-bold text-[#39FF14] uppercase tracking-[0.15em]">
                  <MapPin size={14} />
                  Ubicado en Sevilla | Disponible para proyectos locales y remotos
                </div>
                <div className="text-[9px] md:text-[10px] text-[#39FF14]/40 font-bold uppercase tracking-[0.2em] pl-6 max-w-sm">
                  Servicios de desarrollo web en Sevilla, Dos Hermanas, Aljarafe y toda Andalucía
                </div>
              </div>
            </div>
          </div>
          
          <motion.button 
            onClick={scrollToContact}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="flex items-center gap-3 md:gap-4 text-[#39FF14] group w-full lg:w-auto p-4 md:p-0 border border-[#39FF14]/20 lg:border-none rounded-lg lg:rounded-none text-left cursor-pointer"
          >
            <div className="w-14 h-14 md:w-20 md:h-20 rounded-full border border-[#39FF14]/40 flex items-center justify-center group-hover:bg-[#39FF14] group-hover:text-black transition-all flex-shrink-0">
              <Mail size={24} className="md:w-8 md:h-8" />
            </div>
            <div>
              <span className="block text-[10px] md:text-sm uppercase tracking-widest text-gray-500 font-bold">Envíame un mensaje</span>
              <span className="text-lg md:text-xl lg:text-3xl font-bold font-syncopate uppercase">Hablemos</span>
            </div>
          </motion.button>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-center pt-8 md:pt-12 border-t border-white/5 gap-6 md:gap-8">
          <div className="text-[9px] md:text-xs text-gray-500 font-bold tracking-widest uppercase text-center md:text-left order-3 md:order-1">
            © 2024 JUANMA TENA LUQUE. TODOS LOS DERECHOS RESERVADOS.
          </div>

          <div className="flex items-center gap-6 order-1 md:order-2">
            <a href="https://www.linkedin.com/in/juanmanueltenaluque" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-[#39FF14] transition-colors"><Linkedin size={18} /></a>
            <a href="#" className="text-gray-500 hover:text-[#39FF14] transition-colors"><Twitter size={18} /></a>
            <a href="#" className="text-gray-500 hover:text-[#39FF14] transition-colors"><Instagram size={18} /></a>
          </div>

          <motion.button 
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            whileHover={{ y: -5 }}
            className="flex items-center gap-2 text-[10px] md:text-xs font-bold uppercase tracking-widest text-[#39FF14] order-2 md:order-3"
          >
            Volver Arriba <ArrowUpCircle size={18} />
          </motion.button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
